/**
 * The camera both viewers drive, and the gestures that move it.
 *
 * Extracted rather than written twice. The point cloud and the model already
 * shared `viewerCamera`'s maths; what they did *not* share was the interaction,
 * so a fix to one - and this phase makes several - would have had to be
 * remembered for the other. Two viewers whose controls behave differently on
 * the same gesture is the bug this prevents.
 *
 * What it adds over the previous per-viewer handlers:
 *
 * **A fit that is recomputed, not a constant.** The home camera is derived from
 * the framing and the current aspect ratio, so resizing the viewport, entering
 * the enlarged view or opening the same model on a phone all frame it properly.
 *
 * **Touch.** One finger orbits, two pinch to zoom and drag to pan. Previously
 * `pointermove` was wired for one pointer only, so a phone could rotate a model
 * and nothing else.
 *
 * **Zoom that keeps the subject.** The wheel and the buttons both scale the
 * distance about the current target, clamped to the framed radius, so a few
 * turns cannot push the model out of the frustum and lose it.
 */

import { useCallback, useMemo, useRef } from "react";

import type { Camera, Vec3 } from "./viewerCamera";
import { fitDistance, type Framing } from "./viewerFraming";

/** One wheel notch, and one press of a zoom button. */
const ZOOM_STEP = 1.25;

export interface ViewerCameraHandle {
  /** Read by the render loop every frame. Never state - it changes per mouse-move. */
  camera: { current: Camera };
  /** Attach to a canvas once the framing is known. Returns its own teardown. */
  attach: (canvas: HTMLCanvasElement, framing: Framing, redraw: () => void) => () => void;
  /** Recompute the framing for the current viewport and go there. */
  fit: () => void;
  /** Back to the view the model opened at. */
  reset: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  /** Re-measure after a layout change; the caller still schedules the redraw. */
  refit: () => void;
  /** Point the camera along an axis, keeping its distance and target. */
  setOrientation: (theta: number, phi: number) => void;
}

export function useViewerCamera(): ViewerCameraHandle {
  const camera = useRef<Camera>({ theta: 0.6, phi: 0.3, distance: 3, target: [0, 0, 0] });
  const fitRef = useRef<() => void>(() => {});
  const orientRef = useRef<(theta: number, phi: number) => void>(() => {});
  const resetRef = useRef<() => void>(() => {});
  const zoomRef = useRef<(factor: number) => void>(() => {});
  const refitRef = useRef<() => void>(() => {});

  const attach = useCallback(
    (canvas: HTMLCanvasElement, framing: Framing, redraw: () => void) => {
      const aspect = () => {
        const width = canvas.clientWidth || canvas.width || 1;
        const height = canvas.clientHeight || canvas.height || 1;
        return width / height;
      };

      const framed = (): Camera => ({
        theta: 0.6,
        phi: 0.3,
        distance: fitDistance(framing.radius, aspect()),
        target: [...framing.center] as Vec3,
      });

      // The view the model opened at, so "Görünümü sıfırla" returns somewhere
      // good. It used to return to a hard-coded multiple of the radius, which
      // meant that when the first framing was bad the reset was bad too.
      let home = framed();
      camera.current = { ...home, target: [...home.target] as Vec3 };

      const clampDistance = (value: number) =>
        Math.max(framing.radius * 0.02, Math.min(framing.radius * 80, value));

      const zoom = (factor: number) => {
        camera.current.distance = clampDistance(camera.current.distance * factor);
        redraw();
      };

      orientRef.current = (theta: number, phi: number) => {
        camera.current.theta = theta;
        camera.current.phi = phi;
        redraw();
      };
      fitRef.current = () => {
        home = framed();
        camera.current = { ...home, target: [...home.target] as Vec3 };
        redraw();
      };
      resetRef.current = () => {
        camera.current = { ...home, target: [...home.target] as Vec3 };
        redraw();
      };
      zoomRef.current = zoom;
      // A resize changes the aspect ratio, so the distance that fits changes
      // with it. Only the stored home moves; the user's current view is left
      // where they put it.
      refitRef.current = () => {
        home = { ...framed(), theta: home.theta, phi: home.phi };
      };

      // --- pointers ----------------------------------------------------------
      //
      // Tracked in a map rather than a single "dragging" flag, which is what
      // makes two fingers possible at all.
      const active = new Map<number, { x: number; y: number }>();
      let mode: "orbit" | "pan" | null = null;
      let pinchDistance = 0;

      const orbitBy = (dx: number, dy: number) => {
        camera.current.theta -= dx * 0.008;
        // Short of the poles: at exactly vertical the up vector degenerates
        // and the view flips.
        const limit = Math.PI / 2 - 0.01;
        camera.current.phi = Math.max(-limit, Math.min(limit, camera.current.phi + dy * 0.008));
      };

      const panBy = (dx: number, dy: number) => {
        // Screen space, scaled by distance so the model tracks the finger or
        // cursor at any zoom.
        const scale = camera.current.distance * 0.0015;
        const right: Vec3 = [Math.cos(camera.current.theta), 0, -Math.sin(camera.current.theta)];
        const up: Vec3 = [
          -Math.sin(camera.current.phi) * Math.sin(camera.current.theta),
          Math.cos(camera.current.phi),
          -Math.sin(camera.current.phi) * Math.cos(camera.current.theta),
        ];
        camera.current.target = [
          camera.current.target[0] + (-right[0] * dx + up[0] * dy) * scale,
          camera.current.target[1] + (-right[1] * dx + up[1] * dy) * scale,
          camera.current.target[2] + (-right[2] * dx + up[2] * dy) * scale,
        ];
      };

      const centroid = () => {
        let x = 0;
        let y = 0;
        for (const point of active.values()) {
          x += point.x;
          y += point.y;
        }
        const n = active.size || 1;
        return { x: x / n, y: y / n };
      };

      const spread = () => {
        const points = [...active.values()];
        if (points.length < 2) return 0;
        return Math.hypot(points[0]!.x - points[1]!.x, points[0]!.y - points[1]!.y);
      };

      let last = { x: 0, y: 0 };

      const onPointerDown = (event: PointerEvent) => {
        active.set(event.pointerId, { x: event.clientX, y: event.clientY });
        canvas.setPointerCapture(event.pointerId);
        // Right button or shift pans, one finger or the left button orbits -
        // the convention every 3D tool this product's users have already met.
        mode = active.size >= 2 || event.button === 2 || event.shiftKey ? "pan" : "orbit";
        last = centroid();
        pinchDistance = spread();
      };

      const onPointerMove = (event: PointerEvent) => {
        if (!active.has(event.pointerId)) return;
        active.set(event.pointerId, { x: event.clientX, y: event.clientY });
        const now = centroid();
        const dx = now.x - last.x;
        const dy = now.y - last.y;
        last = now;

        if (active.size >= 2) {
          // Two fingers: the change in separation zooms, the change in
          // midpoint pans. Both at once, as every map and photo viewer does.
          const distance = spread();
          if (pinchDistance > 0 && distance > 0) {
            camera.current.distance = clampDistance(
              camera.current.distance * (pinchDistance / distance),
            );
          }
          pinchDistance = distance;
          panBy(dx, dy);
        } else if (mode === "pan") {
          panBy(dx, dy);
        } else {
          orbitBy(dx, dy);
        }
        redraw();
      };

      const onPointerUp = (event: PointerEvent) => {
        active.delete(event.pointerId);
        if (canvas.hasPointerCapture(event.pointerId)) {
          canvas.releasePointerCapture(event.pointerId);
        }
        if (active.size === 0) mode = null;
        else {
          last = centroid();
          pinchDistance = spread();
        }
      };

      const onWheel = (event: WheelEvent) => {
        event.preventDefault();
        zoom(Math.exp(event.deltaY * 0.001));
      };

      const onContextMenu = (event: Event) => event.preventDefault();

      canvas.addEventListener("pointerdown", onPointerDown);
      canvas.addEventListener("pointermove", onPointerMove);
      canvas.addEventListener("pointerup", onPointerUp);
      canvas.addEventListener("pointercancel", onPointerUp);
      canvas.addEventListener("wheel", onWheel, { passive: false });
      canvas.addEventListener("contextmenu", onContextMenu);

      return () => {
        fitRef.current = () => {};
        resetRef.current = () => {};
        orientRef.current = () => {};
        zoomRef.current = () => {};
        refitRef.current = () => {};
        active.clear();
        canvas.removeEventListener("pointerdown", onPointerDown);
        canvas.removeEventListener("pointermove", onPointerMove);
        canvas.removeEventListener("pointerup", onPointerUp);
        canvas.removeEventListener("pointercancel", onPointerUp);
        canvas.removeEventListener("wheel", onWheel);
        canvas.removeEventListener("contextmenu", onContextMenu);
      };
    },
    [],
  );

  const fit = useCallback(() => fitRef.current(), []);
  const reset = useCallback(() => resetRef.current(), []);
  const zoomIn = useCallback(() => zoomRef.current(1 / ZOOM_STEP), []);
  const zoomOut = useCallback(() => zoomRef.current(ZOOM_STEP), []);
  const refit = useCallback(() => refitRef.current(), []);
  /**
   * Put the camera on an axis view without changing how close it is.
   *
   * Goes through the same redraw the pointer handlers use, so a preset is a
   * camera move like any other and the frame that follows is the one the
   * marker overlay projects against.
   */
  const setOrientation = useCallback((theta: number, phi: number) => {
    orientRef.current(theta, phi);
  }, []);

  // One stable object for the life of the component. The viewers put this in
  // the dependency array of the effect that creates their WebGL context, and a
  // fresh object per render would tear that context down and rebuild it on
  // every state change - three contexts for one cloud.
  return useMemo(
    () => ({ camera, attach, fit, reset, zoomIn, zoomOut, refit, setOrientation }),
    [attach, fit, reset, zoomIn, zoomOut, refit, setOrientation],
  );
}
